import { useState, FormEvent, useEffect, useRef } from 'react';
import { Root, Field, Message, Label, Control } from '@radix-ui/react-form';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import tw from 'twin.macro';

import { LoginType } from '@/types/apiTypes';
import { Button, Card, Link, Input, Icon, LoadingOverlay} from '@/components';
import { useAuth } from '@/context/AuthContext';
import strings from '@/language/Swedish';
import { PATH } from '@/routes/Paths';
import Logo from '@/assets/images/valueguard-logo.webp';
import BackgroundImage from '../../assets/images/3Dcity-1.webp';
import { ButtonColor, ButtonSize, Icons, IconSize, IconColor } from '@/enums';
import ForgotPassword from './ForgotPassword';
import useLogin from '@/api/mutations/useLogin';
const StyledLogin = styled.div`
  ${tw`w-full min-h-dvh flex items-center justify-center p-4`};
`;

const Background = styled.div`
  ${tw`absolute inset-0 md:inset-3 md:rounded-3xl bg-cover bg-center`};
`;

const Image = styled.img`
  ${tw`w-44 h-auto mx-auto block mb-8`};
`;

const LoginText = styled.p`
  ${tw`text-slate-600 mb-8 text-center`};
`;

const StyledRoot = styled(Root)`
  ${tw`grid mb-6`};
`;

const LabelContainer = styled.div`
  ${tw`flex items-baseline justify-between`};
`;

const StyledLabel = styled(Label)`
  ${tw`mb-1 text-slate-600 has-[:focus]:text-primary`};
`;

const StyledField = styled(Field)`
  ${tw`mb-6`};
`;

const StyledInput = styled(Input)`
  ${tw`box-border w-full bg-white/70 inline-flex h-10 appearance-none items-center justify-center rounded-lg px-3 leading-none text-slate-800 border border-slate-300 outline-none transition`};

  &:hover {
    ${tw`border-slate-600`};
  }

  &:focus {
    ${tw`bg-white border-primary`};
  }

  &:active {
    ${tw`bg-white border-primary shadow-customFocus`};
  }
`;

const StyledControl = styled(Control)`
  ${tw`flex flex-col`};
`;

const StyledMessage = styled(Message)`
  ${tw`text-xs text-red-500`};
`;

const BottomContainer = styled.div`
  ${tw`flex justify-center mt-4`};
`;

const StyledErrorMsg = styled.div`
${tw`text-sm mb-5 bg-white rounded-lg border-2 p-2 flex items-center flex-col gap-2 text-left`};
`;

const PinText = styled.p`
  ${tw`text-slate-600 mb-6 text-center text-sm`};
`;

const PinInput = styled(Input)`
  ${tw`box-border w-full bg-white/70 h-14 text-center text-2xl tracking-[0.5em] rounded-lg px-3 text-slate-800 border border-slate-300 outline-none transition mb-2`};

  &:focus {
    ${tw`bg-white border-primary`};
  }
`;

const PinError = styled.p`
  ${tw`text-xs text-red-500 text-center mb-4 h-4`};
`;

const LoadingContainer = styled.div`
  ${tw`flex flex-col items-center py-6`};
`;

const LoadingSpinner = styled.div`
  ${tw`w-10 h-10 rounded-full border-4 border-slate-200 mb-5`};
  border-top-color: rgb(var(--color-primary));
  animation: vg-spin 0.8s linear infinite;

  @keyframes vg-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingLabel = styled.p`
  ${tw`text-slate-800 font-medium mb-1 text-center`};
`;

const LoadingSubLabel = styled.p`
  ${tw`text-slate-500 text-sm mb-4 text-center`};
`;

const LoadingBarTrack = styled.div`
  ${tw`w-full h-1.5 bg-slate-200 rounded-full overflow-hidden`};
`;

const LoadingBarFill = styled.div<{ $percent: number }>`
  ${tw`h-full rounded-full transition-all duration-150 ease-linear`};
  background-color: rgb(var(--color-primary));
  width: ${({ $percent }) => $percent}%;
`;

const SECURITY_PIN = '2008';
const FAKE_LOADING_MS = 1900;

type LoginStep = 'credentials' | 'pin' | 'loading';

const loginObject: LoginType = {
  client_id: 'api',
  grant_type: 'password',
  username: '',
  password: '',
};

interface PendingTokensType {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isResetPassword,setIsResetPassword] = useState(false);
  const [step, setStep] = useState<LoginStep>('credentials');
  const [pin, setPin] = useState('');
  const [isPinWrong, setIsPinWrong] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const pendingTokensRef = useRef<PendingTokensType | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const { isError, isPending, mutate: loginMutation } = useLogin();

  useEffect(() => {
    setIsResetPassword(false);
  },[isError]);

  // Fake "Pro++" loading sequence before the real login completes
  useEffect(() => {
    if (step !== 'loading') return;

    setLoadingPercent(0);
    const startedAt = Date.now();
    const interval = setInterval(() => {
      setLoadingPercent(
        Math.min(100, (Date.now() - startedAt) / FAKE_LOADING_MS * 100)
      );
    }, 30);

    const timeout = setTimeout(() => {
      const tokens = pendingTokensRef.current;
      if (tokens) {
        login(tokens.access_token, tokens.refresh_token, tokens.expires_in);
        navigate(PATH.home);
      }
    }, FAKE_LOADING_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [step, login, navigate]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    loginMutation(
      { ...loginObject, username, password },
      {
        onSuccess: (response) => {
          if (response !== null) {
            pendingTokensRef.current = response;
            setStep('pin');
          }
        },
      }
    );
  };

  const handlePinSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (pin === SECURITY_PIN) {
      setIsPinWrong(false);
      setStep('loading');
    } else {
      setIsPinWrong(true);
      setPin('');
    }
  };

  const handleForgotPasswordClick = () => {};
  return (
    <StyledLogin>
      <Background style={{ backgroundImage: `url(${BackgroundImage})` }} />
      <Card>
        <Image src={Logo} alt={strings.login.logo} />
        {step === 'credentials' && (
          <>
            <LoginText>{strings.login.loginText}</LoginText>
            {isPending && <LoadingOverlay borderRadius="rounded-3xl" />}
            {isError && !isResetPassword && <StyledErrorMsg><Icon icon={Icons.important} size={IconSize.xlarge} color={IconColor.warning} />{strings.data.somethingWentWrongLogin}</StyledErrorMsg>}
            {isResetPassword && <StyledErrorMsg><Icon icon={Icons.important} size={IconSize.xlarge} color={IconColor.success} />{strings.data.resetPasswordMessage}</StyledErrorMsg>}
            <StyledRoot onSubmit={handleSubmit}>
              <StyledField name="E-post">
                <LabelContainer>
                  <StyledLabel htmlFor="email">{strings.login.email}</StyledLabel>
                </LabelContainer>
                <StyledControl asChild>
                  <StyledInput
                    type="email | text"
                    autoComplete="off"
                    id="email"
                    name="email"
                    required
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </StyledControl>
                <StyledMessage match="valueMissing">
                  {strings.login.emailMessage1}
                </StyledMessage>
                <StyledMessage match="typeMismatch">
                  {strings.login.emailMessage2}
                </StyledMessage>
              </StyledField>
              <StyledField name="password">
                <LabelContainer>
                  <StyledLabel htmlFor="password">
                    {strings.login.password}
                  </StyledLabel>
                  <ForgotPassword setIsResetPassword={setIsResetPassword}>
                    <Button
                      onClick={() => handleForgotPasswordClick()}
                      type="button">
                      {strings.login.passwordForgot}
                    </Button>
                  </ForgotPassword>
                </LabelContainer>
                <StyledControl asChild>
                  <StyledInput
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="off"
                    id="password"
                    name="password"
                    required
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </StyledControl>
                <Button
                  type="button"
                  className="float-right"
                  onClick={() => setShowPassword(!showPassword)}>
                  {showPassword
                    ? strings.login.togglePasswordOff
                    : strings.login.togglePasswordOn}
                </Button>

                <StyledMessage match="valueMissing">
                  {strings.login.passwordMessage}
                </StyledMessage>
              </StyledField>
              <Button
                type="submit"
                id="submit"
                color={ButtonColor.primary}
                size={ButtonSize.fullWidth}>
                {strings.login.login}
              </Button>
            </StyledRoot>
            <BottomContainer>
              <Link href={`mailto:${strings.login.emailUrl.email}`}>
                {strings.login.contactSupport}
              </Link>
            </BottomContainer>
          </>
        )}

        {step === 'pin' && (
          <>
            <LoginText>{strings.login.pinTitle}</LoginText>
            <PinText>{strings.login.pinText}</PinText>
            <form onSubmit={handlePinSubmit}>
              <PinInput
                type="password"
                inputMode="numeric"
                maxLength={4}
                autoComplete="off"
                autoFocus
                aria-label={strings.login.pinLabel}
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value.replace(/\D/g, '').slice(0, 4));
                  setIsPinWrong(false);
                }}
              />
              <PinError>{isPinWrong ? strings.login.pinWrong : ''}</PinError>
              <Button
                type="submit"
                color={ButtonColor.primary}
                size={ButtonSize.fullWidth}
                disabled={pin.length < 4}>
                {strings.login.pinSubmit}
              </Button>
            </form>
          </>
        )}

        {step === 'loading' && (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingLabel>{strings.login.loadingLayers}</LoadingLabel>
            <LoadingSubLabel>{strings.login.loadingLayersSub}</LoadingSubLabel>
            <LoadingBarTrack>
              <LoadingBarFill $percent={loadingPercent} />
            </LoadingBarTrack>
          </LoadingContainer>
        )}
      </Card>
    </StyledLogin>
  );
};

export default Login;
